import React from 'react';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { formatarMoeda } from '../../utils/formatters';

export default function EvolucaoComparativa({ resultado, valorFinanciado, prazoMeses, onVoltar, onExportarPDF }) {
  const bancosOriginal = resultado?.simulacoes || [];
  const bancos = bancosOriginal.map((b) => ({
    ...b,
    parcelas: b.parcelas.filter((p) => p.numero > 0),
  }));
  const maxParcelas = Math.max(...bancos.map((b) => b.parcelas?.length || 0));
  const menorTotal = Math.min(...bancos.map((b) => b.totais.totalAPagar));

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: '1.3rem', fontWeight: 700, marginBottom: 4 }}>
            📊 Evolução Comparativa das Parcelas
          </h2>
          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
            Valor Financiado: {formatarMoeda(valorFinanciado)} | Prazo: {prazoMeses} meses
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="ghost" onClick={onVoltar}>← Voltar ao Simulador</Button>
          <Button variant="danger" onClick={onExportarPDF}>📄 Exportar PDF</Button>
        </div>
      </div>

      {/* Resumo dos Bancos */}
      <Card style={{ marginBottom: 20, overflowX: 'auto' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 16 }}>Resumo da Simulação</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: 'var(--surface)' }}>
              <th style={{ padding: 12, textAlign: 'left', color: 'var(--text-muted)' }}>Resumo</th>
              {bancos.map((b, i) => (
                <th key={i} style={{ padding: 12, textAlign: 'center', color: 'var(--blue)', fontWeight: 600 }}>
                  {b.nomeBanco.split(' ')[0]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { label: 'Taxa de Juros', render: (b) => <span style={{ color: 'var(--gold)', fontWeight: 600 }}>{b.taxaJurosAnual.toFixed(2)}%</span> },
              { label: 'Primeira Parcela', render: (b) => <span style={{ color: 'var(--blue)' }}>{formatarMoeda(b.informacoes.primeiraParcela)}</span> },
              { label: 'Última Parcela', render: (b) => <span style={{ color: 'var(--purple)' }}>{formatarMoeda(b.informacoes.ultimaParcela)}</span> },
              {
                label: 'Total a Pagar',
                render: (b) => {
                  const isMelhor = b.totais.totalAPagar === menorTotal;
                  return (
                    <span style={{
                      color: isMelhor ? '#059669' : 'var(--text)',
                      fontWeight: 700,
                      background: isMelhor ? 'rgba(0,214,143,0.08)' : 'transparent',
                      padding: '2px 6px',
                      borderRadius: 4,
                    }}>
                      {formatarMoeda(b.totais.totalAPagar)}{isMelhor && ' ✓'}
                    </span>
                  );
                },
              },
            ].map((row) => (
              <tr key={row.label} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: 12, color: 'var(--text-muted)' }}>{row.label}</td>
                {bancos.map((b, i) => (
                  <td key={i} style={{ padding: 12, textAlign: 'center' }}>{row.render(b)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Tabela de Evolução */}
      <Card style={{ marginBottom: 20, overflowX: 'auto' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 16 }}>
          Evolução das Parcelas — Todos os Bancos
        </h3>
        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr style={{ background: 'var(--surface)' }}>
                <th style={{
                  padding: '12px 8px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600,
                  position: 'sticky', left: 0, background: 'var(--surface-alt)', minWidth: 60,
                }}>Parcela</th>
                {bancos.map((b, i) => (
                  <th key={i} style={{ padding: '12px 8px', textAlign: 'center', color: 'var(--blue)', fontWeight: 600, minWidth: 100 }}>
                    {b.nomeBanco.split(' ')[0]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: maxParcelas }, (_, idx) => {
                const numeroParcela = bancos[0]?.parcelas[idx]?.numero ?? idx;
                const valores = bancos.map((x) => x.parcelas[idx]?.valorParcela || 0).filter((v) => v > 0);
                const menorValor = valores.length ? Math.min(...valores) : 0;

                return (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border)', background: idx % 2 === 0 ? 'var(--white)' : 'var(--surface)' }}>
                    <td style={{
                      padding: '10px 8px', textAlign: 'center', color: 'var(--text-muted)', fontWeight: 600,
                      position: 'sticky', left: 0,
                      background: idx % 2 === 0 ? 'var(--white)' : 'var(--surface)',
                    }}>
                      {String(numeroParcela).padStart(3, '0')}
                    </td>
                    {bancos.map((b, i) => {
                      const parcela = b.parcelas[idx];
                      const isMenor = parcela?.valorParcela === menorValor && parcela?.valorParcela > 0;
                      return (
                        <td key={i} style={{
                          padding: '10px 8px', textAlign: 'center',
                          color: isMenor ? '#059669' : 'var(--text)',
                          fontWeight: isMenor ? 600 : 400,
                          background: isMenor ? 'rgba(0,214,143,0.06)' : 'transparent',
                        }}>
                          {parcela ? formatarMoeda(parcela.valorParcela) : '-'}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Soma por Período */}
      <Card style={{ overflowX: 'auto' }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 700, marginBottom: 16 }}>
          Soma de Valores por Período (12 meses)
        </h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: 'var(--surface)' }}>
              <th style={{ padding: 12, textAlign: 'left', color: 'var(--text-muted)' }}>Período</th>
              {bancos.map((b, i) => (
                <th key={i} style={{ padding: 12, textAlign: 'center', color: 'var(--blue)', fontWeight: 600 }}>
                  {b.nomeBanco.split(' ')[0]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: Math.ceil(prazoMeses / 12) }, (_, periodoIdx) => {
              const inicio = periodoIdx * 12 + 1;
              const fim = Math.min((periodoIdx + 1) * 12, prazoMeses);
              const somasPorBanco = bancos.map((b) =>
                b.parcelas.filter((p) => p.numero >= inicio && p.numero <= fim).reduce((acc, p) => acc + p.valorParcela, 0)
              );
              const menorSoma = Math.min(...somasPorBanco.filter((s) => s > 0));

              return (
                <tr key={periodoIdx} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: 12, color: 'var(--text-muted)' }}>
                    Parcela {String(inicio).padStart(3, '0')} - {String(fim).padStart(3, '0')}
                  </td>
                  {somasPorBanco.map((soma, i) => {
                    const isMenor = soma === menorSoma && soma > 0;
                    return (
                      <td key={i} style={{
                        padding: 12, textAlign: 'center',
                        color: isMenor ? '#059669' : 'var(--text)',
                        fontWeight: isMenor ? 600 : 400,
                        background: isMenor ? 'rgba(0,214,143,0.06)' : 'transparent',
                      }}>
                        {formatarMoeda(soma)}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}